import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Image,
  InputGroup,
  Text,
  useToast,
} from "@chakra-ui/react";
import Input from "../components/inputs/Input";
import { Center, Flex } from "@chakra-ui/layout";
import loginImage from "../assets/login.png";
import Logo from "../components/Logo";
import PrimaryButton from "../components/PrimaryButton";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { login } from "../mutations/auth.mutations";
import { attachTokenToClient } from "../client";
import { NavLink, useNavigate } from "react-router-dom";
import PasswordInput from "../components/inputs/PasswordInput";
import StlLoader from "../components/StlLoader";
import { useQueryClient } from "react-query";
import InfoIcon from "../components/icons/InfoIcon";
import { useCenters } from "../hooks/centers.hooks";

function LoginPage() {
  const methods = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrentCenter } = useCenters()

  const onSubmit = async (data: FieldValues) => {
    try {
      const token = await login({ email: data.email, password: data.password });
      if (token.type == "bearer") {
        queryClient.invalidateQueries('me')
        queryClient.removeQueries();
        setCurrentCenter(null)
        attachTokenToClient(token.token);
      } else {
        toast({
          title: "Connexion",
          description: "Type de token non pris en charge",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      navigate("/dashboard");
    } catch (err) {
      methods.setError("email", { message: "Erreur d'email ou de mot de passe." });
      methods.setError("password", { message: "Erreur d'email ou de mot de passe." });
    }
  };

  return (
    <Flex>
      <Box display={{ base: 'none', lg: 'block' }}>
        <StlLoader
          alternative={
            <Image
              src={loginImage}
              alt="Dan Abramov"
              h="100vh"
              w="50vw"
              display={{ base: "none", lg: "block" }}
            />
          }
        ></StlLoader>
      </Box>
      <Center w="100%">
        <Flex
          direction="column"
          alignItems="left"
          justifyContent="center"
          h="100vh"
          w="100%"
          px={{ base: "44px", md: "144px" }}
        >
          <Flex direction="column" alignItems="center">
            <Logo />
            <Heading
              fontWeight={600}
              fontSize="32px"
              lineHeight="48px"
              color="gray.900"
              whiteSpace={"nowrap"}
            >
              Connectez-vous
            </Heading>
            <Text
              fontWeight={400}
              fontSize="20px"
              lineHeight="30px"
              color="gray.600"
              textAlign="center"
              display={{ base: "none", sm: "block" }}
            >
              Entrez vos informations afin de vous connecter à votre espace.
            </Text>
          </Flex>
          <FormProvider {...methods}>
            <form
              style={{ marginTop: "32px" }}
              onSubmit={methods.handleSubmit((data) => onSubmit(data))}
            >
              <FormControl isInvalid={!!methods.formState.errors.email}>
                <Input
                  id="email"
                  placeholder="Email"
                  {...methods.register("email", {
                    required: "Ce champ est obligatoire",
                  })}
                ></Input>
                <FormErrorMessage>
                  <>{methods.formState.errors?.email?.message}</>
                </FormErrorMessage>
              </FormControl>

              <FormControl
                mt="10px"
                isInvalid={!!methods.formState.errors.password}
              >
                <InputGroup size="md">
                  <PasswordInput
                    pr="4.5rem"
                    placeholder="Mot de passe"
                    {...methods.register("password", {
                      required: "Ce champ est obligatoire",
                    })}
                  />
                </InputGroup>
                <FormErrorMessage>
                  <>{methods.formState.errors?.email?.message}</>
                </FormErrorMessage>
              </FormControl>

              <Box
                my="24px"
                color="brand.700"
                w="fit-content"
                _hover={{ textDecoration: "underline" }}
              >
                <NavLink to="/password/reset">Mot de passe oublié</NavLink>
              </Box>

              <PrimaryButton mt="10px" type="submit">
                Se connecter
              </PrimaryButton>
            </form>
          </FormProvider>
          <Center my="24px">
            Pas encore de compte ?&nbsp;
            <Button variant="link" color="brand.700">
              <NavLink to="/signin">S'inscrire</NavLink>
            </Button>
          </Center>

          <Center>
            <Flex backgroundColor="brand.50" border="solid 1px" borderColor="brand.300" p={4} gap="4" alignItems="center" borderRadius="lg">
              <Box>
                <InfoIcon w="40px" h="40px" />
              </Box>
              <Box>
                <Text lineHeight='21px' fontSize='14px' fontWeight='600' color='gray.800'>Vous êtes audioprothésiste ou assistante et souhaitez rejoindre un centre ? Veuillez contacter celui-ci pour obtenir une invitation.</Text>
              </Box>
            </Flex>
          </Center>
        </Flex>
      </Center>
    </Flex>
  );
}

export default LoginPage;
